import React, { useState } from 'react';
import { ocorrenciaService } from '../../services/api';
import { showSuccess, showError } from '../../utils/toast';
import PageHeader from '../ui/PageHeader';
import Button from '../ui/Button';
import './Ocorrencias.css';

/**
 * Sessão 24 — Módulo Ocorrências MVP (TD104).
 *
 * Form pra criar nova ocorrência. Campos mínimos pra registrar atrito baixo:
 * descricao (obrigatório), origem (default 'outro'), reclamante e contexto opcionais.
 * Sem anexos no MVP — vêm na NC após promover.
 */
function OcorrenciaForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({
    descricao: '',
    origem: 'outro',
    cliente_reclamante: '',
    produto: '',
    lote: '',
    data_ocorrencia: '',
    contexto_processo: '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.descricao.trim()) {
      showError('Descrição é obrigatória.');
      return;
    }
    setSaving(true);
    try {
      // Remove campos vazios antes de enviar (backend aceita null/undefined)
      const payload = Object.fromEntries(
        Object.entries(form).filter(([_, v]) => v !== '' && v !== null)
      );
      const created = await ocorrenciaService.criar(payload);
      showSuccess(`Ocorrência ${created.numero_ocorrencia} criada.`);
      if (onCreated) onCreated(created);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      showError(`Erro ao criar ocorrência: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="oc-form-container">
      <PageHeader
        title="Nova ocorrência"
        actions={
          <Button variant="secondary" size="sm" icon="arrow_back" onClick={onCancel} disabled={saving}>
            Voltar
          </Button>
        }
      />

      <p className="oc-help">
        Use Ocorrência pra registrar reclamações menores, desvios internos ou
        observações de auditoria que <strong>ainda não justificam abrir NC formal</strong>.
        Você pode promover pra NC mais tarde se necessário.
      </p>

      <form className="oc-form" onSubmit={handleSubmit}>
        <div className="oc-form-group">
          <label htmlFor="oc-descricao">Descrição *</label>
          <textarea
            id="oc-descricao"
            name="descricao"
            value={form.descricao}
            onChange={handleChange}
            rows={4}
            required
            disabled={saving}
            placeholder="O que aconteceu? Quanto mais detalhes, melhor a triagem."
            maxLength={5000}
          />
        </div>

        <div className="oc-form-row">
          <div className="oc-form-group">
            <label htmlFor="oc-origem">Origem *</label>
            <select
              id="oc-origem"
              name="origem"
              value={form.origem}
              onChange={handleChange}
              disabled={saving}
            >
              <option value="reclamacao_cliente">Reclamação de cliente</option>
              <option value="desvio_interno">Desvio interno</option>
              <option value="auditoria">Auditoria</option>
              <option value="outro">Outro</option>
            </select>
          </div>

          {form.origem === 'reclamacao_cliente' && (
            <div className="oc-form-group">
              <label htmlFor="oc-reclamante">Cliente Reclamante</label>
              <input
                id="oc-reclamante"
                type="text"
                name="cliente_reclamante"
                value={form.cliente_reclamante}
                onChange={handleChange}
                disabled={saving}
                placeholder="Nome da empresa/contato"
                maxLength={255}
              />
            </div>
          )}
        </div>

        <div className="oc-form-row">
          <div className="oc-form-group">
            <label htmlFor="oc-produto">Produto (opcional)</label>
            <input
              id="oc-produto"
              type="text"
              name="produto"
              value={form.produto}
              onChange={handleChange}
              disabled={saving}
              placeholder="Ex: Vigota C30, lote LT-001"
              maxLength={255}
            />
          </div>

          <div className="oc-form-group">
            <label htmlFor="oc-lote">Lote (opcional)</label>
            <input
              id="oc-lote"
              type="text"
              name="lote"
              value={form.lote}
              onChange={handleChange}
              disabled={saving}
              maxLength={255}
            />
          </div>

          <div className="oc-form-group">
            <label htmlFor="oc-data">Data ocorrência (opcional)</label>
            <input
              id="oc-data"
              type="date"
              name="data_ocorrencia"
              value={form.data_ocorrencia}
              onChange={handleChange}
              disabled={saving}
            />
          </div>
        </div>

        <div className="oc-form-group">
          <label htmlFor="oc-contexto">Situação atual do processo (opcional)</label>
          <textarea
            id="oc-contexto"
            name="contexto_processo"
            value={form.contexto_processo}
            onChange={handleChange}
            rows={3}
            disabled={saving}
            placeholder="Detalhes do processo onde ocorreu — ajuda na triagem."
            maxLength={5000}
          />
        </div>

        <div className="oc-form-actions">
          <Button variant="secondary" type="button" onClick={onCancel} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" icon="check" disabled={saving}>
            {saving ? 'Salvando…' : 'Criar ocorrência'}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default OcorrenciaForm;
